//
// Copyright 2020 Emerald Associates, Inc.  All Rights Reserved.
//
// Use of this file other than by Emerald Associates, Inc. is forbidden
// unless otherwise authorized by a separate written license agreement.
//
// $Id$
//
import React from "react";
import {makeStyles} from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import IconButton from "@material-ui/core/IconButton";
import RemoveIcon from "@material-ui/icons/Delete";
import * as Utils from "eai-configurator-ui/components/Utils";
import Tooltip from "@material-ui/core/Tooltip";
import Button from "@material-ui/core/Button";
import DownloadIcon from "@material-ui/icons/GetApp";
import Link from "@material-ui/core/Link";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  paper: {
    width: "100%",
    marginBottom: theme.spacing(2),
  },
  button: {
    marginTop: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
  table: {
    minWidth: 300,
  },
  toolbar: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1),
  },
  title: {
    flex: "1 1 100%",
  },
}));

export default function AutomateJobsTable({
                                            setAlertMessage,
                                            refreshCounter,
                                            handleRemoveJob
                                          }) {
  const classes = useStyles();
  const [automateJobs, setAutomateJobs] = React.useState([]);
  const [scheduledTimeoutId, setScheduledTimeoutId] = React.useState(null);
  const componentIsMounted = React.useRef(true);

  // Job to repopulate the table
  React.useEffect(() => {
    populateTable(true);
    return () => {
      componentIsMounted.current = false;
      if (scheduledTimeoutId) {
        clearTimeout(scheduledTimeoutId);
      }
    };
  }, []);

  const rescheduleTimeout = () => {
    if (scheduledTimeoutId) {
      clearTimeout(scheduledTimeoutId);
    }
    if (componentIsMounted.current) {
      setScheduledTimeoutId(setTimeout(() => populateTable(true), 5000));
    }
  };

  React.useEffect(() => {
    populateTable(true);
  }, [refreshCounter]);

  const populateTable = (shouldRescheduleTimeout) => {
    Utils.fetchData("loader/automateJobs")
      .then((data) => {
        setAutomateJobs(data);
      })
      .catch(function (response) {
        Utils.getErrorMessage(response, "Failed to fetch jobs.").then(
          (message) =>
            setAlertMessage({
              success: false,
              message: message,
            })
        );
      }).finally(function () {
      if (shouldRescheduleTimeout) {
        rescheduleTimeout();
      }
    });
  };

  const isTableNotEmpty = automateJobs && automateJobs.length > 0;
  const isTableEmpty = !isTableNotEmpty;

  return (
    <div className={classes.root}>
      <Paper className={classes.paper}>
        <TableContainer>
          <Table className={classes.table}>
            {isTableNotEmpty && (
              <TableHead>
                <TableRow>
                  <TableCell>Job Database</TableCell>
                  <TableCell align="center">Date</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Task/ File Count</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
            )}
            <TableBody>
              {automateJobs && automateJobs.map((automateJob, index) => (
                <TableRow key={index} hover role="task" tabIndex={-1}>
                  <TableCell
                    align="left"
                    component="th"
                    scope="row"
                    padding="default"
                  >
                    {automateJob.jobDatabase}
                  </TableCell>
                  <TableCell
                    align="center"
                    component="th"
                    scope="row"
                    padding="default"
                  >
                    {automateJob.jobDate}
                  </TableCell>
                  <TableCell
                    align="center"
                    component="th"
                    scope="row"
                    padding="default"
                  >
                    {automateJob.status}
                  </TableCell>
                  <TableCell
                    align="center"
                    component="th"
                    scope="row"
                    padding="default"
                  >
                    {automateJob.jobCount}
                  </TableCell>
                  <TableCell
                    align="right"
                    component="th"
                    scope="row"
                    padding="default"
                  >
                    {automateJob.hasDownload && (
                      <Link download={automateJob.jobFileName} href={Utils.getBaseUrl() + "loader/downloadJobFile?jobTaskKey=" + automateJob.jobTaskKey}>
                        <Button
                          variant="contained"
                          color={"primary"}
                          className={classes.button}
                          startIcon={<DownloadIcon/>}
                        >
                          Download Zip
                        </Button>
                      </Link>
                    )}
                    <Tooltip title="Delete Job">
                      <IconButton
                        onClick={() => {
                          handleRemoveJob(automateJob);
                        }}
                      >
                        <RemoveIcon/>
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {isTableEmpty && (
                <TableRow style={{height: 53}}>
                  <TableCell>
                    <Typography align="center" color="textSecondary">
                      No jobs
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </div>
  );
}
